export type AppLauncherManifestEntry = {
  name: string;
  icon: string;
  link: string;
};

type NonEmptyAppLauncherManifest = readonly [
  AppLauncherManifestEntry,
  ...AppLauncherManifestEntry[],
];

const DEFAULT_APP_LAUNCHER_MANIFEST: NonEmptyAppLauncherManifest = [
  {
    name: "Platform",
    icon: "/platform-logo.svg",
    link: "/",
  },
  {
    name: "Nazare",
    icon: "/favicon.svg",
    link: "https://nazare.app/",
  },
];

function isManifestEntry(value: unknown): value is AppLauncherManifestEntry {
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.name === "string" &&
    entry.name.trim().length > 0 &&
    typeof entry.icon === "string" &&
    entry.icon.trim().length > 0 &&
    typeof entry.link === "string" &&
    entry.link.trim().length > 0
  );
}

/**
 * Reads the launcher manifest from a JSON environment variable. Invalid or
 * empty manifests fall back atomically so a partial deployment cannot leave
 * the launcher with missing apps.
 */
export function parseAppLauncherManifest(
  rawManifest: string | undefined,
): NonEmptyAppLauncherManifest {
  if (!rawManifest) return DEFAULT_APP_LAUNCHER_MANIFEST;

  try {
    const parsed: unknown = JSON.parse(rawManifest);
    if (!Array.isArray(parsed) || !parsed.every(isManifestEntry)) {
      return DEFAULT_APP_LAUNCHER_MANIFEST;
    }
    return parsed.length > 0
      ? (parsed as unknown as NonEmptyAppLauncherManifest)
      : DEFAULT_APP_LAUNCHER_MANIFEST;
  } catch {
    return DEFAULT_APP_LAUNCHER_MANIFEST;
  }
}

export function isCurrentAppLink(link: string, currentUrl: string): boolean {
  try {
    return new URL(link, currentUrl).origin === new URL(currentUrl).origin;
  } catch {
    return false;
  }
}
